import {
    ArrowDownToLine,
    ArrowUpFromLine,
    ChevronDown,
    Keyboard,
    Loader2,
    Minus,
    Plus,
    RotateCcw,
    SlidersHorizontal,
} from 'lucide-react';
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';

import LegalLinks from '@/components/LegalLinks';
import { Button } from '@/components/ui/button';
import {
    InsufficientCustodialBanner,
    isSnappyOnboarding,
    LegacyWalletWithdraw,
    useCustodialPanelState,
    useSnappyWalletState,
    ZeroCustodialPrompt,
} from '@/features/custodial';
// the store module only: the flow barrel also carries the flow's dialogs.
import { useFlowStore } from '@/features/flow/store';
import {
    findMarket,
    useTokenPairDetail,
    useTokenPairs,
    useTokenPairStore,
} from '@/features/tokenPairs';
import { cn } from '@/lib/utils';

import { useHasUnmanagedActiveTrades } from '../hooks/useActiveUserTrades';
import { useExecutionDisplay } from '../hooks/useExecutionDisplay';
import { useRestingOrder } from '../hooks/useRestingOrder';
import { useLivePnL } from '../hooks/useLivePnL';
import { useLivePosition } from '../hooks/useLivePosition';
import type { PriceConfig } from '../hooks/usePriceConfig';
import { useSpeedConfig } from '../hooks/useSpeedConfig';
import { useTradeQuote } from '../hooks/useTradeQuote';
import { useTriggerPriceMode } from '../hooks/useTriggerPriceMode';
import {
    selectActiveTradeVisual,
    selectCanAfford,
    selectCustodialShortfall,
    selectEffectiveMaxCollateral,
    selectEffectiveMinCollateral,
    selectHasCustodialWarning,
    selectHasRealOpenTrade,
    selectIsRealMode,
    selectIsTradeDisabled,
    selectLeverageConstraintMessage,
    selectShowZeroCustodialPrompt,
    selectTradeDisabledReason,
    useTradingStore,
} from '../store';
import type { PositionType } from '../types';
import { getLeverageScale, getNearestLeverageOption } from '../utils';
import { getMaintenanceRate } from '../utils/maintenanceRate';
import { estimateLiquidationPrice } from '../utils/pnl';
import {
    liquidatesFirstStopLossHint,
    longOnlyTakeProfitHint,
    triggerPriceFrom,
    triggerSummaryLabel,
} from '../utils/triggerSetting';

import ActivateHip3Banner from './ActivateHip3Banner';
import { LeverageSlider } from './LeverageSlider';
import {
    LimitPriceField,
    OrderTypeTabs,
    parseLimitPriceInput,
} from './LimitOrderControls';
import RestingOrderCard from './RestingOrderCard';
import PositionMarginEditor from './PositionMarginEditor';
import PositionTriggerEditor from './PositionTriggerEditor';
import ReactivateAgentBanner from './ReactivateAgentBanner';
import RestoreTriggersIndicator from './RestoreTriggersIndicator';
import ShortcutsModal from './ShortcutsModal';
import SnappyOnboardingBanner from './SnappyOnboardingBanner';
import V2ShortfallPrompt from './V2ShortfallPrompt';

// The browser wallet flow's (V2) account banner (restore, deposit, approve), in the place the V1 banners take below.
// Lazy: it lives in the funds module, which a V1 session never downloads.
const V2FundsBanner = lazy(
    async () => import('@/features/browserWallet/funds/V2FundsBanner'),
);

/**
 * Desktop trade rail (the `panel` grid area): ENTER AMOUNT header
 * (balance + deposit/withdraw), big amount box, 25/50/75/MAX, SET
 * LEVERAGE / TAKE PROFIT / STOP LOSS / SLIPPAGE chip rows, order-overview
 * meta rows, skewed LONG/SHORT, and the PositionDetails + CLOSE flow
 * while a position is open. All state goes through the trading store's
 * actions/selectors (the option arrays mirror TradeConfigToggles).
 */

/**
 * A trigger row on the rail: the ROI presets plus a `$` chip that swaps them for
 * a price field.
 *
 * The rail draws chips where the mobile panel draws a ToggleGroup, so the two
 * share `useTriggerPriceMode` rather than markup. They did share nothing at all,
 * which is how the panel got price entry and the rail silently did not.
 */
const TriggerChips: React.FC<{
    label: string;
    options: readonly string[];
    value: string;
    onChange: (next: string) => void;
    markPrice: number;
    disabled?: boolean;
    /** caption under the chips, e.g. which presets a short cannot reach */
    hint?: string | null;
}> = ({ label, options, value, onChange, markPrice, disabled, hint }) => {
    const { priceMode, draft, enterPriceMode, choosePreset, setDraft } =
        useTriggerPriceMode(value, onChange);

    return (
        <div>
            <SectionLabel>{label}</SectionLabel>
            <div className="flex gap-2">
                {options.map((opt) => (
                    <Chip
                        key={opt}
                        active={!priceMode && value === opt}
                        disabled={disabled}
                        onClick={() => choosePreset(opt)}
                    >
                        {opt}
                    </Chip>
                ))}
                <Chip
                    active={priceMode}
                    disabled={disabled}
                    onClick={enterPriceMode}
                    ariaLabel={`${label} at a price`}
                >
                    $
                </Chip>
            </div>
            {priceMode && (
                <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min={0}
                    autoFocus
                    disabled={disabled}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    aria-label={`${label} price`}
                    placeholder={
                        markPrice > 0
                            ? `Price (mark ${markPrice.toFixed(2)})`
                            : 'Price'
                    }
                    className="mt-2 h-9 w-full rounded-lg border border-border bg-input px-2.5 font-mono text-sm tabular-nums text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50"
                />
            )}
            {hint && (
                <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>
            )}
        </div>
    );
};

const TAKE_PROFIT_OPTIONS = ['Off', '100%', '200%', '300%'] as const;
const STOP_LOSS_OPTIONS = ['Off', '25%', '50%', '75%'] as const;
const SLIPPAGE_OPTIONS = [
    { label: '0.5%', bps: 50 },
    { label: '1%', bps: 100 },
    { label: '2%', bps: 200 },
] as const;

// TP/SL/slippage hide behind the summary dropdown; the expansion
// preference is shared with TradeConfigToggles (same storage key).
const CONFIG_EXPANDED_KEY = 'snappy.configExpanded.v1';

// Mirrors the store's INITIAL_DEMO_BALANCE (module-private there), which is
// what a demo-mode reset restores.
const DEMO_START_BALANCE = 100;

function loadConfigExpanded(): boolean {
    try {
        return localStorage.getItem(CONFIG_EXPANDED_KEY) === '1';
    } catch {
        return false;
    }
}

interface SpeedTradeRailProps {
    currentValue: number;
    priceConfig: PriceConfig;
    onOpenPosition: (type: PositionType, amount: number) => void;
    onClosePosition: () => void;
    /** Drops the charted market's resting limit order. */
    onCancelLimitOrder: () => void;
    /** Takes the charted market's resting limit order at market. */
    onFillLimitOrder: () => void;
    isIsolatedOnly: boolean;
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => (
    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {children}
    </span>
);

const Chip: React.FC<{
    active?: boolean;
    disabled?: boolean;
    onClick: () => void;
    children: React.ReactNode;
    /** for chips whose label is a bare symbol, like the `$` price toggle */
    ariaLabel?: string;
}> = ({ active, disabled, onClick, children, ariaLabel }) => (
    <button
        type="button"
        disabled={disabled}
        aria-pressed={active}
        aria-label={ariaLabel}
        onClick={onClick}
        className={cn(
            'flex-1 cursor-pointer rounded-full border py-1.5 text-center font-mono text-[11px] font-semibold tabular-nums transition-colors',
            active
                ? 'border-primary bg-primary/10 text-primary shadow-[0_0_12px_rgba(211,255,18,0.3)]'
                : 'border-border bg-input text-muted-foreground hover:text-foreground',
            disabled && 'cursor-not-allowed opacity-50',
        )}
    >
        {children}
    </button>
);

const MetaRow: React.FC<{
    label: React.ReactNode;
    value: string;
    valueClass?: string;
}> = ({ label, value, valueClass }) => (
    <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span
            className={cn(
                'font-mono text-[11px] font-medium tabular-nums text-foreground/90',
                valueClass,
            )}
        >
            {value}
        </span>
    </div>
);

function fmtUsd(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}`;
}

/** "14:23:41 · 19 Jul" — open time for the position overview. */
function fmtOpenedAt(timestamp: number | null): string {
    if (timestamp === null || !Number.isFinite(timestamp)) return '—';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '—';
    const time = date.toLocaleTimeString('en-GB', { hour12: false });
    const day = date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
    });
    return `${time} · ${day}`;
}

const SpeedTradeRail: React.FC<SpeedTradeRailProps> = ({
    currentValue,
    priceConfig,
    onOpenPosition,
    onClosePosition,
    onCancelLimitOrder,
    onFillLimitOrder,
    isIsolatedOnly,
}) => {
    // ── Store reads ──────────────────────────────────────────────────────
    const balance = useTradingStore((s) => s.balance);
    // the tradeable part of the balance (already on HyperCore); the total above it includes USDC
    // still on the HyperEVM custodial/adapter wallets that a fast open cannot spend.
    const custodialAvailable = useTradingStore((s) => s.custodialAvailable);
    const isCustodialBalanceLoadingForHint = useTradingStore(
        (s) => s.isCustodialBalanceLoading,
    );
    const betAmount = useTradingStore((s) => s.betAmount);
    const adjustBet = useTradingStore((s) => s.adjustBet);
    const setDirectBetAmount = useTradingStore((s) => s.setDirectBetAmount);
    const setPercentage = useTradingStore((s) => s.setPercentage);
    const setDemoBalance = useTradingStore((s) => s.setDemoBalance);
    const shortcutsEnabled = useTradingStore((s) => s.shortcutsEnabled);
    const activePosition = useTradingStore(selectActiveTradeVisual);
    const isRealMode = useTradingStore(selectIsRealMode);
    // The browser wallet flow (V2) has no custodial wallet, no agent and no
    // service-wallet setup: the V1 account banners below stay off there, and
    // its own banner takes their place (restore, deposit, approval).
    const isBrowserWalletFlow = useFlowStore((s) => s.active === 'v2');
    // V1 only: the gap is USDC still on HyperEVM (custodial wallet + adapter).
    // In V2 the total and the free part differ by the margin in open
    // positions, which this sentence would misdescribe.
    const showsTradeableHint =
        isRealMode &&
        !isBrowserWalletFlow &&
        !isCustodialBalanceLoadingForHint &&
        balance - custodialAvailable >= 0.01;
    // the only real floor is HL's $10 order notional, i.e. $10 / leverage of
    // margin (backend minCollateralUsd). a null bound (no quote yet) must not
    // trigger the banner, and a hardcoded $10 misinforms anyone with $2-$10.
    const minCollateralUsd = useTradingStore(selectEffectiveMinCollateral);
    const balanceBelowMin =
        minCollateralUsd !== null && balance < minCollateralUsd;
    const canAffordStore = useTradingStore(selectCanAfford);
    const isTradeDisabled = useTradingStore(selectIsTradeDisabled);
    const hasRealOpenTrade = useTradingStore(selectHasRealOpenTrade);
    const custodialShortfall = useTradingStore(selectCustodialShortfall);
    const hasCustodialWarning = useTradingStore(selectHasCustodialWarning);
    const isCustodialBalanceProvisional = useTradingStore(
        (s) => s.isCustodialBalanceProvisional,
    );
    const zeroCustodialPromptEligible = useTradingStore(
        selectShowZeroCustodialPrompt,
    );
    // Why the CTAs are disabled — surfaced under the LONG/SHORT pair so a
    // greyed-out button is never unexplained.
    const leverageConstraintMessage = useTradingStore(
        selectLeverageConstraintMessage,
    );
    const resumeUnverified = useTradingStore((s) => s.resumeUnverified);
    const tradeDisabledReason = useTradingStore(selectTradeDisabledReason);
    // Caps the "+" stepper (the store clamps regardless — this is the
    // affordance that says so before the click).
    const effectiveMaxCollateral = useTradingStore(
        selectEffectiveMaxCollateral,
    );
    const isCustodialBalanceLoading = useTradingStore(
        (s) => s.isCustodialBalanceLoading,
    );
    const isCustodialBalanceSettling = useTradingStore(
        (s) => s.isCustodialBalanceSettling,
    );

    const leverage = useTradingStore((s) => s.leverage);
    const leverageAuto = useTradingStore((s) => s.leverageAuto);
    const setLeverage = useTradingStore((s) => s.setLeverage);
    const chooseLeverage = useTradingStore((s) => s.chooseLeverage);
    const takeProfit = useTradingStore((s) => s.takeProfit);
    const setTakeProfit = useTradingStore((s) => s.setTakeProfit);
    const stopLoss = useTradingStore((s) => s.stopLoss);
    const setStopLoss = useTradingStore((s) => s.setStopLoss);
    const slippageBps = useTradingStore((s) => s.slippageBps);
    const setSlippageBps = useTradingStore((s) => s.setSlippageBps);
    const orderType = useTradingStore((s) => s.orderType);
    const setOrderType = useTradingStore((s) => s.setOrderType);
    const limitPriceDraft = useTradingStore((s) => s.limitPrice);
    const setLimitPrice = useTradingStore((s) => s.setLimitPrice);
    // the charted market's resting limit order, real or demo.
    const restingOrder = useRestingOrder();

    const hasUnmanagedTrades = useHasUnmanagedActiveTrades();
    const { executionInFlight, executionLabel } = useExecutionDisplay();
    const { livePnLValue, livePnLPercent } = useLivePnL(currentValue);
    const quote = useTradeQuote(currentValue);
    const live = useLivePosition();
    const { data: speedConfig } = useSpeedConfig();
    const isTestnet = speedConfig?.testnet ?? false;

    const [showShortcuts, setShowShortcuts] = useState(false);
    // Raw text of the amount field while the user types, paired with the
    // store amount it produced. Keeping the text lets a half-entered decimal
    // ("11.", which a number input reports as "") survive the round trip
    // through parseFloat; the paired amount makes the draft self-invalidating
    // — a stepper, quick-pick or clamp writing a different amount drops it.
    const [amountDraft, setAmountDraft] = useState<{
        text: string;
        amount: number;
    } | null>(null);
    const [configExpanded, setConfigExpanded] = useState(loadConfigExpanded);
    const toggleConfig = () => {
        setConfigExpanded((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(CONFIG_EXPANDED_KEY, next ? '1' : '0');
            } catch {
                // preference just won't persist
            }
            return next;
        });
    };

    // ── Custodial gating ─────────────────────────────────────────────────
    const openDeposit = useCustodialPanelState((s) => s.openDeposit);
    const openWithdraw = useCustodialPanelState((s) => s.openWithdraw);
    // while the user has no wallet yet (S2a/S2b) the onboarding prompt owns the instruction;
    // the zero-prompt's "deposit once" copy and the deposit nag below are only correct for a
    // wallet that already exists and is empty. (this predated the snappy-state matrix in the v4
    // branch — the gate mirrors the final StatsPanel logic the matrix tests pin.)
    const snappy = useSnappyWalletState();
    const snappyOnboarding = isSnappyOnboarding(snappy);
    // V2's empty account is the browser wallet banner's "Deposit USDC to start."
    const showZeroCustodialPrompt =
        zeroCustodialPromptEligible &&
        !snappyOnboarding &&
        !isBrowserWalletFlow;

    // ── Leverage ladder (mirrors TradeConfigToggles) ─────────────────────
    const networkId = useTokenPairStore((s) => s.networkId);
    const selectedPair = useTokenPairStore((s) => s.selectedPair);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    const { data: pairDetail } = useTokenPairDetail(networkId, selectedPair);
    const { data: marketsData } = useTokenPairs(networkId);
    const marketMaxLeverage = useMemo(
        () =>
            findMarket(marketsData?.markets, selectedCoin)?.maxLeverage ?? null,
        [marketsData, selectedCoin],
    );

    const leverageScale = useMemo(() => {
        const scale = getLeverageScale(pairDetail?.leverage);
        if (scale.length > 0) return scale;
        if (marketMaxLeverage !== null && marketMaxLeverage >= 2) {
            return Array.from(
                { length: Math.floor(marketMaxLeverage) - 1 },
                (_, i) => i + 2,
            );
        }
        return [];
    }, [pairDetail?.leverage, marketMaxLeverage]);

    // Keep leverage on the pair's real ladder (same clamp semantics as
    // TradeConfigToggles, which is not mounted in this variant).
    useEffect(() => {
        if (leverageScale.length === 0) return;
        const nextLeverage = leverageAuto
            ? leverageScale[leverageScale.length - 1]
            : getNearestLeverageOption(leverageScale, leverage);
        if (nextLeverage !== leverage) {
            setLeverage(nextLeverage);
        }
    }, [leverageScale, leverage, leverageAuto, setLeverage]);

    // ── Derived order meta ───────────────────────────────────────────────
    const isLimitMode = orderType === 'limit';
    const limitPriceValue = isLimitMode
        ? parseLimitPriceInput(limitPriceDraft)
        : null;
    const canAfford =
        canAffordStore &&
        !isTradeDisabled &&
        !hasUnmanagedTrades &&
        // a limit order with no price is not an order
        (!isLimitMode || limitPriceValue !== null);
    const showCloseView = activePosition !== null || hasRealOpenTrade;
    // The resting card replaces the FORM, never the position view: once the
    // order fills there is a position, and that always wins.
    const showRestingView = !showCloseView && restingOrder !== null;

    // ── Open-position readout (mirrors PositionDetails' derivations, with
    //    client-side fallbacks so every row has a value in demo mode too) ─
    const posType =
        activePosition?.type ??
        (live.isLong === null ? 'LONG' : live.isLong ? 'LONG' : 'SHORT');
    const posEntry = live.entryPx ?? activePosition?.entryPrice ?? null;
    const posLeverage = live.leverage ?? activePosition?.leverage ?? null;
    const posMargin = live.marginUsed ?? activePosition?.amount ?? null;
    const posSize =
        live.size ??
        (posMargin !== null && posLeverage !== null && posEntry
            ? (posMargin * posLeverage) / posEntry
            : null);
    const posLiqIsReal = live.liquidationPx !== null;
    const posLiq =
        live.liquidationPx ??
        (posEntry !== null && posLeverage !== null
            ? estimateLiquidationPrice(
                  posEntry,
                  posType,
                  posLeverage,
                  getMaintenanceRate(
                      pairDetail?.leverage,
                      marketMaxLeverage,
                      posMargin !== null ? posMargin * posLeverage : null,
                  ),
              )
            : null);
    // Position value: backend first; else size × mark price.
    const posValue =
        live.positionValue ??
        (posSize !== null && currentValue > 0 ? posSize * currentValue : null);

    // the MARK, not the quote's slippage-padded limit: TP targets derived from
    // the padded price sat ~1% high for longs and pointed the wrong way for
    // shorts (both legs were centred on the long-side pad).
    //
    // In limit mode the LIMIT PRICE is the entry the order will get, so every
    // derived figure below (TP/SL targets, liquidation) is centred on it
    // instead — the same reference the backend resolves the bps legs against.
    const entryEstimate = limitPriceValue ?? quote.markPx ?? currentValue;
    const slippageLabel =
        SLIPPAGE_OPTIONS.find((o) => o.bps === slippageBps)?.label ??
        `${slippageBps / 100}%`;
    // worst fill either direction will accept — the number the quote's
    // `limitPx` actually is, resolved per side instead of long-only.
    const { slipCapLong, slipCapShort } = quote;
    // An ROI preset resolves against the expected entry; an absolute price is
    // already the answer and reads the same for both directions.
    const tpLong = triggerPriceFrom(
        takeProfit,
        entryEstimate,
        leverage,
        'LONG',
        true,
    );
    const tpShort = triggerPriceFrom(
        takeProfit,
        entryEstimate,
        leverage,
        'SHORT',
        true,
    );

    // A limit order is not quoted by the backend (nothing to quote: the price is
    // given), so its sizing is computed here from the price the user named.
    const notionalUsd = betAmount > 0 ? betAmount * leverage : null;
    // The market's margin terms at this order's notional tier. Used for the
    // limit-order liquidation estimate below AND for the stop-loss hint, which
    // needs to know where the exchange would force-close the position.
    const maintenanceRate = getMaintenanceRate(
        pairDetail?.leverage,
        marketMaxLeverage,
        notionalUsd,
    );
    const shownSize =
        isLimitMode && limitPriceValue !== null && notionalUsd !== null
            ? notionalUsd / limitPriceValue
            : quote.sizeAsset;
    const shownOrderValue =
        isLimitMode && limitPriceValue !== null
            ? notionalUsd
            : quote.orderValueUsd;
    const shownLiqLong =
        isLimitMode && limitPriceValue !== null
            ? estimateLiquidationPrice(
                  limitPriceValue,
                  'LONG',
                  leverage,
                  maintenanceRate,
              )
            : quote.estLiqLong;
    const shownLiqShort =
        isLimitMode && limitPriceValue !== null
            ? estimateLiquidationPrice(
                  limitPriceValue,
                  'SHORT',
                  leverage,
                  maintenanceRate,
              )
            : quote.estLiqShort;

    return (
        <div className="flex h-full flex-col gap-3 p-3">
            {/* Available balance — prominent, visible in BOTH the order
                form and the open-position view so the user always knows
                what they have to work with. */}
            <div className="rounded-xl border border-border bg-secondary/30 px-3.5 py-2.5">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            Available balance
                        </span>
                        {/* Spinner while the custodial balance is in flight,
                            dimmed pulse while it settles after a trade — the
                            number must never read as authoritative early. */}
                        <span
                            className={cn(
                                'font-mono text-2xl font-semibold tabular-nums tracking-tight text-foreground',
                                isRealMode &&
                                    isCustodialBalanceSettling &&
                                    'animate-pulse opacity-60',
                                // read succeeded, trading authority still resolving: show the real
                                // number immediately rather than a 0 or a spinner, dimmed so it does
                                // not read as "ready to trade" yet.
                                isCustodialBalanceProvisional && 'opacity-50',
                            )}
                        >
                            {isCustodialBalanceLoading ? (
                                <Loader2
                                    size={20}
                                    className="inline animate-spin text-muted-foreground"
                                />
                            ) : (
                                <>
                                    ${balance.toFixed(2)}{' '}
                                    <span className="text-xs font-medium text-muted-foreground">
                                        USDC
                                    </span>
                                </>
                            )}
                        </span>
                        {showsTradeableHint && (
                            <span className="text-[10px] text-muted-foreground">
                                Tradeable now ${custodialAvailable.toFixed(2)},
                                the rest is still on HyperEVM
                            </span>
                        )}
                    </div>
                    {isRealMode && (
                        <div className="flex flex-col gap-1.5">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => openDeposit()}
                                className="h-7 gap-1.5 px-2.5 text-[11px] font-semibold"
                            >
                                <ArrowDownToLine size={12} />
                                Deposit
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => openWithdraw()}
                                className="h-7 gap-1.5 px-2.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
                            >
                                <ArrowUpFromLine size={12} />
                                Withdraw
                            </Button>
                        </div>
                    )}
                </div>
                {balanceBelowMin &&
                    !activePosition &&
                    !snappyOnboarding &&
                    (isRealMode ? (
                        <p className="mt-1.5 text-xs text-amber-400/90">
                            {balance > 0
                                ? `Balance is below the $${minCollateralUsd.toFixed(2)} minimum at ${leverage}x. Deposit USDC or raise leverage.`
                                : 'Deposit USDC to start trading.'}
                        </p>
                    ) : (
                        // Demo has no deposit affordance, so telling a demo user
                        // to deposit is a dead end. Offer the top-up instead.
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDemoBalance(DEMO_START_BALANCE)}
                            className="mt-1.5 h-7 px-2.5 text-[11px] font-semibold"
                        >
                            <RotateCcw size={12} />
                            Reset demo balance
                        </Button>
                    ))}
            </div>

            {/* Section header + keyboard shortcuts */}
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {showCloseView
                        ? 'Position'
                        : showRestingView
                          ? 'Limit order'
                          : 'Enter amount'}
                </span>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setShowShortcuts(true)}
                    title="Keyboard Shortcuts"
                >
                    <Keyboard size={14} />
                </Button>
            </div>

            {/* Onboarding / recovery / activation banners. the onboarding banner had its only
                desktop mount in StatsPanel, which the v1 removal deleted — without this a fresh
                social-login user gets no path into wallet setup from the trade rail. */}
            {isBrowserWalletFlow ? (
                <Suspense fallback={null}>
                    <V2FundsBanner />
                </Suspense>
            ) : (
                <>
                    <SnappyOnboardingBanner />
                    <LegacyWalletWithdraw />
                    <ReactivateAgentBanner />
                    <ActivateHip3Banner />
                </>
            )}
            {/* both flows: the missing-leg restore goes through the backend
                seam (MoonHub in V1, the browser wallet's signer in V2) */}
            <RestoreTriggersIndicator />

            {showCloseView ? (
                <>
                    {/* Live PnL headline */}
                    {activePosition && (
                        <div className="flex items-baseline justify-between rounded-xl border border-border bg-secondary/30 px-3 py-2.5">
                            <span
                                className={cn(
                                    'font-mono text-2xl font-bold tabular-nums',
                                    livePnLValue >= 0
                                        ? 'text-success'
                                        : 'text-destructive',
                                )}
                            >
                                {livePnLValue >= 0 ? '+' : ''}$
                                {livePnLValue.toFixed(2)}
                            </span>
                            <span
                                className={cn(
                                    'font-mono text-sm font-bold tabular-nums',
                                    livePnLValue >= 0
                                        ? 'text-success'
                                        : 'text-destructive',
                                )}
                            >
                                {livePnLValue >= 0 ? '+' : ''}
                                {livePnLPercent.toFixed(2)}%
                            </span>
                        </div>
                    )}

                    {/* Position overview — same row layout as the order
                        overview, every value present in demo mode too.
                        The box and the profit-reflecting values tint by
                        the live PnL sign (win = lime, loss = pink). */}
                    <div
                        className={cn(
                            'flex flex-col gap-1.5 rounded-xl border bg-secondary/30 p-3 transition-colors',
                            livePnLValue >= 0
                                ? 'border-success/35'
                                : 'border-destructive/35',
                        )}
                    >
                        <div className="mb-0.5 flex items-center justify-between">
                            <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                Position overview
                            </span>
                            <span
                                className={cn(
                                    'rounded-full px-2 py-0.5 text-[9px] font-bold uppercase',
                                    posType === 'LONG'
                                        ? 'bg-primary/15 text-primary'
                                        : 'bg-destructive/15 text-destructive',
                                )}
                            >
                                {posType === 'LONG' ? '▲ Long' : '▼ Short'}
                                {posLeverage !== null && ` ${posLeverage}x`}
                            </span>
                        </div>
                        <MetaRow
                            label="Opened"
                            value={fmtOpenedAt(
                                activePosition?.timestamp ?? null,
                            )}
                        />
                        <MetaRow
                            label="Entry price"
                            value={
                                posEntry !== null
                                    ? `$${priceConfig.formatDisplay(posEntry)}`
                                    : '—'
                            }
                        />
                        <MetaRow
                            label="Mark price"
                            value={`$${priceConfig.formatDisplay(
                                // the exchange's mark — the value its PnL above
                                // was priced at. `currentValue` is the feed's
                                // LAST price, which is a different number.
                                activePosition?.markPrice ?? currentValue,
                            )}`}
                            valueClass={
                                livePnLValue >= 0
                                    ? 'text-success'
                                    : 'text-destructive'
                            }
                        />
                        <MetaRow
                            label={
                                <>
                                    Liq. price{' '}
                                    {!posLiqIsReal && (
                                        <em className="not-italic text-muted-foreground/70">
                                            (est.)
                                        </em>
                                    )}
                                </>
                            }
                            value={
                                posLiq !== null
                                    ? `$${priceConfig.formatDisplay(posLiq)}`
                                    : '—'
                            }
                            valueClass="text-destructive"
                        />
                        <MetaRow label="Margin" value={fmtUsd(posMargin)} />
                        <MetaRow
                            label="Size"
                            value={
                                posSize !== null
                                    ? `${posSize.toFixed(6)} ${selectedCoin}`
                                    : '—'
                            }
                        />
                        <MetaRow
                            label="Position value"
                            value={fmtUsd(posValue)}
                            valueClass={
                                livePnLValue >= 0
                                    ? 'text-success font-semibold'
                                    : 'text-destructive font-semibold'
                            }
                        />
                        <MetaRow
                            label="Unrealized PnL"
                            value={`${livePnLValue >= 0 ? '+' : ''}$${livePnLValue.toFixed(2)} (${livePnLValue >= 0 ? '+' : ''}${livePnLPercent.toFixed(2)}%)`}
                            valueClass={
                                livePnLValue >= 0
                                    ? 'text-success font-semibold'
                                    : 'text-destructive font-semibold'
                            }
                        />
                    </div>

                    {/* Post-open TP/SL editor — the trade's REAL installed
                        triggers, editable in place. Renders nothing in demo
                        mode or when no Open trade matches the coin. */}
                    <PositionTriggerEditor currentValue={currentValue} />
                    {/* Add margin from the free HyperCore balance into the
                        isolated position. Same gating as the TP/SL editor. */}
                    <PositionMarginEditor />

                    <Button
                        variant={
                            livePnLValue >= 0 ? 'cyber-long' : 'cyber-short'
                        }
                        size="lg"
                        onClick={onClosePosition}
                        disabled={executionInFlight}
                        className="st-close-flat flex h-auto w-full flex-col gap-0.5 py-3"
                    >
                        {executionInFlight ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                <span className="text-xs font-semibold">
                                    {executionLabel}
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="flex items-center gap-2 text-sm font-bold uppercase">
                                    Close
                                    <span className="font-mono tabular-nums">
                                        {livePnLValue >= 0 ? '+' : ''}$
                                        {livePnLValue.toFixed(2)} (
                                        {livePnLValue >= 0 ? '+' : ''}
                                        {livePnLPercent.toFixed(2)}%)
                                    </span>
                                </span>
                            </>
                        )}
                    </Button>
                </>
            ) : showRestingView && restingOrder !== null ? (
                <RestingOrderCard
                    order={restingOrder}
                    markPrice={currentValue}
                    formatPrice={priceConfig.formatDisplay}
                    onCancel={onCancelLimitOrder}
                    onFill={onFillLimitOrder}
                    busy={executionInFlight}
                />
            ) : showZeroCustodialPrompt ? (
                <ZeroCustodialPrompt onFund={() => openDeposit()} />
            ) : (
                <>
                    {/* Amount box */}
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="icon"
                            aria-label="Decrease amount by $10"
                            onClick={() => adjustBet(-10)}
                            disabled={betAmount <= 0 || executionInFlight}
                            className="h-12 w-12 shrink-0"
                        >
                            <Minus size={16} />
                        </Button>
                        <div className="relative flex-1">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-muted-foreground">
                                $
                            </span>
                            <input
                                type="number"
                                aria-label="Amount in USD"
                                // Never blank: with nothing typed the field
                                // reads 0, so an empty box can't masquerade as
                                // a funding problem.
                                value={
                                    amountDraft?.amount === betAmount
                                        ? amountDraft.text
                                        : String(betAmount)
                                }
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === '') {
                                        setDirectBetAmount(0);
                                        setAmountDraft({ text: '', amount: 0 });
                                        return;
                                    }
                                    const value = parseFloat(raw);
                                    if (!isNaN(value)) {
                                        setDirectBetAmount(value);
                                        setAmountDraft({
                                            text: raw,
                                            amount: useTradingStore.getState()
                                                .betAmount,
                                        });
                                    }
                                }}
                                // Editing over: show the committed amount,
                                // cleared back to 0 rather than left empty.
                                onBlur={() => setAmountDraft(null)}
                                min={0}
                                step="0.01"
                                disabled={executionInFlight}
                                className="h-12 w-full rounded-xl border border-border bg-input px-8 text-center font-mono text-xl font-bold tabular-nums text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50"
                            />
                        </div>
                        <Button
                            variant="outline"
                            size="icon"
                            aria-label="Increase amount by $10"
                            onClick={() => adjustBet(10)}
                            disabled={
                                (effectiveMaxCollateral !== null &&
                                    betAmount >= effectiveMaxCollateral) ||
                                executionInFlight
                            }
                            className="h-12 w-12 shrink-0"
                        >
                            <Plus size={16} />
                        </Button>
                    </div>

                    {/* Percent quick-picks */}
                    <div className="grid grid-cols-4 gap-2">
                        {([0.25, 0.5, 0.75, 1] as const).map((pct) => (
                            <Chip
                                key={pct}
                                disabled={balance === 0 || executionInFlight}
                                onClick={() => setPercentage(pct)}
                            >
                                {pct === 1 ? 'MAX' : `${pct * 100}%`}
                            </Chip>
                        ))}
                    </div>

                    {/* SET LEVERAGE — the real ladder slider (as in v1),
                        always visible */}
                    <div>
                        <SectionLabel>Set leverage</SectionLabel>
                        <LeverageSlider
                            scale={leverageScale}
                            value={leverage}
                            onCommit={chooseLeverage}
                            disabled={executionInFlight}
                            editable
                        />
                    </div>

                    {/* ORDER TYPE — market (IOC at mark) or limit (GTC at a
                        price the user names). Sits under the leverage slider,
                        above the armed-policy summary: it is part of the same
                        "how this order goes out" stack. */}
                    <div>
                        <SectionLabel>Order type</SectionLabel>
                        <OrderTypeTabs
                            value={orderType}
                            onChange={setOrderType}
                            disabled={executionInFlight}
                        />
                    </div>

                    {isLimitMode && (
                        <LimitPriceField
                            value={limitPriceDraft}
                            onChange={setLimitPrice}
                            markPrice={currentValue}
                            formatPrice={priceConfig.formatDisplay}
                            disabled={executionInFlight}
                        />
                    )}

                    {/* TP / SL / slippage behind the summary dropdown */}
                    <button
                        type="button"
                        onClick={toggleConfig}
                        aria-expanded={configExpanded}
                        className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-border/60 bg-secondary/30 px-2.5 py-2 text-left transition-colors hover:bg-secondary/60"
                    >
                        <SlidersHorizontal
                            size={11}
                            className="shrink-0 text-muted-foreground"
                        />
                        <span className="min-w-0 flex-1 truncate font-mono text-[10px] tabular-nums text-muted-foreground">
                            TP{' '}
                            <span
                                className={cn(
                                    'font-bold',
                                    takeProfit !== 'Off'
                                        ? 'text-primary'
                                        : 'text-foreground',
                                )}
                            >
                                {triggerSummaryLabel(
                                    takeProfit,
                                    priceConfig.formatDisplay,
                                )}
                            </span>
                            {' · '}SL{' '}
                            <span className="font-bold text-foreground">
                                {triggerSummaryLabel(
                                    stopLoss,
                                    priceConfig.formatDisplay,
                                )}
                            </span>
                            {' · '}Slip{' '}
                            <span className="font-bold text-foreground">
                                {slippageLabel}
                            </span>
                        </span>
                        <ChevronDown
                            size={12}
                            className={cn(
                                'shrink-0 text-muted-foreground transition-transform duration-200',
                                configExpanded && 'rotate-180',
                            )}
                        />
                    </button>

                    {configExpanded && (
                        <>
                            {/* SET TAKE PROFIT — presets, or a price */}
                            <TriggerChips
                                label="Set take profit"
                                options={TAKE_PROFIT_OPTIONS}
                                value={takeProfit}
                                onChange={setTakeProfit}
                                markPrice={entryEstimate}
                                disabled={executionInFlight}
                                hint={longOnlyTakeProfitHint(
                                    TAKE_PROFIT_OPTIONS,
                                    leverage,
                                )}
                            />

                            {/* SET STOP LOSS */}
                            <TriggerChips
                                label="Set stop loss"
                                options={STOP_LOSS_OPTIONS}
                                value={stopLoss}
                                onChange={setStopLoss}
                                markPrice={entryEstimate}
                                disabled={executionInFlight}
                                hint={liquidatesFirstStopLossHint(
                                    STOP_LOSS_OPTIONS,
                                    leverage,
                                    maintenanceRate,
                                )}
                            />

                            {/* SLIPPAGE */}
                            <div>
                                <SectionLabel>Slippage</SectionLabel>
                                <div className="flex gap-2">
                                    {SLIPPAGE_OPTIONS.map((opt) => (
                                        <Chip
                                            key={opt.bps}
                                            active={slippageBps === opt.bps}
                                            disabled={executionInFlight}
                                            onClick={() =>
                                                setSlippageBps(opt.bps)
                                            }
                                        >
                                            {opt.label}
                                        </Chip>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {isIsolatedOnly && (
                        <p className="rounded-lg border border-sky-400/20 bg-sky-400/5 px-3 py-2 text-xs text-sky-200/80">
                            Isolated margin, opening may take a little longer
                            while leverage is configured.
                        </p>
                    )}

                    {/* Custodial shortfall warning (V2: the same ask without
                        the custodial wording) */}
                    {hasCustodialWarning &&
                        custodialShortfall > 0 &&
                        (isBrowserWalletFlow ? (
                            <V2ShortfallPrompt
                                shortfall={custodialShortfall}
                                onDeposit={() =>
                                    openDeposit(custodialShortfall)
                                }
                            />
                        ) : (
                            <InsufficientCustodialBanner
                                shortfall={custodialShortfall}
                                onDeposit={() =>
                                    openDeposit(custodialShortfall)
                                }
                            />
                        ))}

                    {/* Order overview */}
                    {betAmount > 0 && (
                        <div className="flex flex-col gap-1.5 rounded-xl border border-border/60 bg-secondary/30 p-3">
                            <div className="mb-0.5 flex items-center justify-between">
                                <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                    Order overview
                                </span>
                                {quote.isLoading && (
                                    <Loader2
                                        size={10}
                                        className="animate-spin text-muted-foreground"
                                    />
                                )}
                            </div>
                            <MetaRow
                                label="Size"
                                value={
                                    shownSize !== null
                                        ? `${shownSize.toFixed(6)} ${selectedCoin}`
                                        : '—'
                                }
                            />
                            <MetaRow
                                label="Notional"
                                value={fmtUsd(quote.notionalUsd)}
                            />
                            <MetaRow
                                label="Order value"
                                value={fmtUsd(shownOrderValue)}
                            />
                            {/* A limit order HAS an entry price: the one the
                                user typed. Estimating it would be pretending
                                not to know. */}
                            <MetaRow
                                label={
                                    isLimitMode
                                        ? 'Limit price'
                                        : 'Est. entry price'
                                }
                                value={
                                    isLimitMode
                                        ? limitPriceValue !== null
                                            ? `$${priceConfig.formatDisplay(limitPriceValue)}`
                                            : '—'
                                        : quote.markPx !== null
                                          ? `$${priceConfig.formatDisplay(quote.markPx)}`
                                          : '—'
                                }
                            />
                            {/* A limit order has no slippage: the price IS the
                                bound, so there is no worst fill to report. */}
                            {!isLimitMode &&
                                slipCapLong !== null &&
                                slipCapShort !== null && (
                                    <MetaRow
                                        label={`Max slippage (${slippageLabel})`}
                                        value={`$${priceConfig.formatDisplay(slipCapLong)} / $${priceConfig.formatDisplay(slipCapShort)}`}
                                    />
                                )}
                            {/* the short leg is a dash, not "$0.0000", when the
                                preset needs the price at zero (see triggerPriceFrom) */}
                            {tpLong !== null && (
                                <MetaRow
                                    label={`Take profit (${triggerSummaryLabel(takeProfit, priceConfig.formatDisplay)})`}
                                    value={`$${priceConfig.formatDisplay(tpLong)} / ${
                                        tpShort !== null
                                            ? `$${priceConfig.formatDisplay(tpShort)}`
                                            : '—'
                                    }`}
                                />
                            )}
                            <MetaRow
                                label={
                                    <>
                                        Est. liq. price{' '}
                                        <em className="not-italic text-primary">
                                            (long)
                                        </em>
                                    </>
                                }
                                value={
                                    shownLiqLong !== null
                                        ? `$${priceConfig.formatDisplay(shownLiqLong)}`
                                        : '—'
                                }
                            />
                            <MetaRow
                                label={
                                    <>
                                        Est. liq. price{' '}
                                        <em className="not-italic text-destructive">
                                            (short)
                                        </em>
                                    </>
                                }
                                value={
                                    shownLiqShort !== null
                                        ? `$${priceConfig.formatDisplay(shownLiqShort)}`
                                        : '—'
                                }
                            />
                        </div>
                    )}

                    {/* LONG / SHORT — directly below the order overview,
                        joined pair (outer corners rounded, inner square) */}
                    <div className="grid grid-cols-2 gap-0">
                        <Button
                            variant="cyber-long"
                            size="lg"
                            onClick={() =>
                                canAfford && onOpenPosition('LONG', betAmount)
                            }
                            disabled={!canAfford || executionInFlight}
                            className="st-cta-joined flex h-auto flex-col gap-0.5 py-3"
                        >
                            {executionInFlight ? (
                                <>
                                    <Loader2
                                        size={20}
                                        className="animate-spin"
                                    />
                                    <span className="text-xs font-semibold">
                                        {executionLabel}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="text-base font-black tracking-wide">
                                        Long
                                    </span>
                                    {/* In limit mode the price the button will
                                        place replaces the shortcut hint: what
                                        this click does is the more useful fact. */}
                                    {isLimitMode ? (
                                        <span className="font-mono text-[9px] font-medium tracking-[0.08em]">
                                            {limitPriceValue !== null
                                                ? `at $${priceConfig.formatDisplay(limitPriceValue)}`
                                                : 'set a price'}
                                        </span>
                                    ) : (
                                        shortcutsEnabled && (
                                            <span className="font-mono text-[9px] font-medium tracking-[0.08em]">
                                                L or ↑
                                            </span>
                                        )
                                    )}
                                </>
                            )}
                        </Button>
                        <Button
                            variant="cyber-short"
                            size="lg"
                            onClick={() =>
                                canAfford && onOpenPosition('SHORT', betAmount)
                            }
                            disabled={!canAfford || executionInFlight}
                            className="st-cta-joined flex h-auto flex-col gap-0.5 py-3"
                        >
                            {executionInFlight ? (
                                <>
                                    <Loader2
                                        size={20}
                                        className="animate-spin"
                                    />
                                    <span className="text-xs font-semibold">
                                        {executionLabel}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="text-base font-black tracking-wide">
                                        Short
                                    </span>
                                    {isLimitMode ? (
                                        <span className="font-mono text-[9px] font-medium tracking-[0.08em]">
                                            {limitPriceValue !== null
                                                ? `at $${priceConfig.formatDisplay(limitPriceValue)}`
                                                : 'set a price'}
                                        </span>
                                    ) : (
                                        shortcutsEnabled && (
                                            <span className="font-mono text-[9px] font-medium tracking-[0.08em]">
                                                S or ↓
                                            </span>
                                        )
                                    )}
                                </>
                            )}
                        </Button>
                    </div>

                    {/* Why the CTAs are disabled. Ordered by severity: an
                        unresolved open trade blocks everything, then an
                        unverified resume, then the store-level trade blockers
                        (missing pair data / unresolved trading authority / agent
                        reactivation, which otherwise disable the buttons with
                        no explanation at all), then
                        the pair's collateral bounds, then plain
                        affordability. */}
                    {!executionInFlight &&
                        (hasUnmanagedTrades ? (
                            <p className="text-center text-[11px] text-destructive">
                                An open position isn't in sync yet. Refresh if
                                it doesn't clear.
                            </p>
                        ) : resumeUnverified ? (
                            <p className="text-center text-[11px] text-muted-foreground">
                                Syncing trade state…
                            </p>
                        ) : tradeDisabledReason === 'pairData' ? (
                            <p className="text-center text-[11px] text-destructive">
                                Market data for this pair is unavailable right
                                now. Trading is paused until it loads.
                            </p>
                        ) : tradeDisabledReason === 'authorityResolving' ? (
                            <p className="text-center text-[11px] text-muted-foreground">
                                Finishing sign in, trading unlocks in a moment.
                            </p>
                        ) : tradeDisabledReason === 'reactivating' ? (
                            <p className="text-center text-[11px] text-muted-foreground">
                                Wallet setup in progress. Trading unlocks when
                                it finishes.
                            </p>
                        ) : isLimitMode && limitPriceValue === null ? (
                            <p className="text-center text-[11px] text-muted-foreground">
                                Enter a limit price to place the order.
                            </p>
                        ) : (
                            (leverageConstraintMessage ||
                                (betAmount > 0 &&
                                    !canAffordStore &&
                                    !hasCustodialWarning &&
                                    'Insufficient funds for this position size')) && (
                                <p className="text-center text-[11px] text-destructive">
                                    {leverageConstraintMessage ??
                                        'Insufficient funds for this position size'}
                                </p>
                            )
                        ))}
                </>
            )}

            {/* Environment footer, pinned to the rail's bottom: the TESTNET
                badge (so play money is never mistaken for real) above the
                policy links. Chain-load telemetry moved to the terminal
                header, next to the connection pill it belongs with.
                Kept compact deliberately: this column already overflows on
                short viewports, so any padding here pushes the links into
                the clipped region rather than making room. */}
            <div className="mt-auto flex flex-col items-center gap-1.5 pt-1">
                {isTestnet && (
                    <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-amber-400/80">
                        Testnet
                    </p>
                )}
                <LegalLinks />
            </div>

            {showShortcuts && (
                <ShortcutsModal onClose={() => setShowShortcuts(false)} />
            )}
        </div>
    );
};

export default SpeedTradeRail;
